// Division -> presentation tokens for the standalone transition banner.
// Colors and copy match a reference design POC (six mockups, one per
// division/variant combination). All six combinations are captured directly
// below and exercised in divisions.test.ts.
//
// Color is fixed per division. Copy has two variants selected by
// `data-is-divisional-page`; for AHS the "divisional page" variant isn't just
// different wording, it swaps to a different brand name entirely (Arrowhead
// Intermediaries) - that's an intentional quirk of the reference, not a typo.

export const BANNER_COLORS = {
  AHU: "#136853",
  BSG: "#0e5db0",
  AHS: "#007db5",
} as const;

export type BannerDivisionCode = keyof typeof BANNER_COLORS;

/** Keyed by division, then by "true"/"false" for data-is-divisional-page. */
export const BANNER_COPY: Record<BannerDivisionCode, Record<"true" | "false", string>> = {
  AHU: {
    false: "Our next chapter with Arrowhead Underwriting",
    true: "Our next chapter as Arrowhead Underwriting",
  },
  AHS: {
    false: "Our next chapter with Arrowhead Specialty",
    true: "Our next chapter with Arrowhead Intermediaries",
  },
  BSG: {
    false: "Our next chapter with Bridge Specialty Group",
    true: "Our next chapter: a simpler customer experience",
  },
};

export interface BannerTreatment {
  color: string;
  copy: string;
}

/** Resolve a division + variant to {color, copy}, or undefined if the division is unknown.
 * Case-insensitive - `data-division="ahu"` resolves the same as `"AHU"`. */
export function resolveBannerTreatment(
  code: string | null | undefined,
  isDivisionalPage: boolean,
): BannerTreatment | undefined {
  if (code === null || code === undefined) {
    return undefined;
  }
  const upper = code.toUpperCase();
  if (!(upper in BANNER_COLORS)) {
    return undefined;
  }
  const division = upper as BannerDivisionCode;
  return {
    color: BANNER_COLORS[division],
    copy: BANNER_COPY[division][isDivisionalPage ? "true" : "false"],
  };
}
